/**
 * @author      Dario Casertano <dario@casertano.name>
 * @copyright   Copyright (c) 2021-2024 Casertano Dario – All rights reserved.
 * @license     Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International.
 *
 * @link https://github.com/axios/axios#request-config
 */

import { HttpCodes } from "@/@stdlib/network";
import { captureException } from "@/libraries/bugsnag";
import { settings } from "@/libraries/storage";
import { router } from "@/router";
import AppStore from "@/vuex/modules/AppStore";
import { AxiosResponse, default as _axios, InternalAxiosRequestConfig } from "axios";
import { assign } from "lodash";
import { DateTime } from "luxon";
import { verify, VerifyErrors } from "jsonwebtoken";

const {
    VUE_APP_API,
    VUE_APP_IPFS_GATEWAY,
} = process.env

export const CancelToken = _axios.CancelToken

const axios = _axios.create({
    baseURL: VUE_APP_API,
})

axios.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (config.url?.startsWith(VUE_APP_IPFS_GATEWAY as string) ||
        config.url?.endsWith('/licenses.txt')
    ) {
        return config
    }

    config.url = config.url?.replace(/:lang/i, AppStore.state.appLanguage)

    config.headers = assign({}, config.headers, {
        'Accept': 'application/json',
        'Accept-Language': AppStore.state.appLanguage,
        'Content-Type': 'application/json',
        'x-timezone': DateTime.local().zoneName,
    })

    if (settings.has('auth', 'user', 'auth')) {
        config.headers['Authorization'] = `Bearer ${settings.get('auth', 'user', 'auth')}`
    }

    if (settings.has('system', 'app', 'uuid')) {
        config.headers['x-sender'] = settings.get('system', 'app', 'uuid')
    }

    return config
}, (error) => Promise.reject(error))

const forceLogout = async (): Promise<void> => {
    settings.del('auth', 'user', 'auth')

    await router.push('/login').catch(() => { /** Silence! I know what I do! */ })
}

axios.interceptors.response.use(async (response: AxiosResponse): Promise<AxiosResponse> => {
    if (response.headers[ 'x-api-version' ]) {
        settings.set('system', 'api', 'version', response.headers[ 'x-api-version' ])
    }

    if (response.status === HttpCodes.OK) {
        const {
            code,
            sign,
        } = response.data

        if (code) {
            verify(sign, settings.get('system', 'app', 'uuid'), {
                algorithms: ['HS256'],
                issuer: new URL(axios.defaults.baseURL as string).hostname,
                subject: settings.get('system', 'app', 'uuid'),
            }, async (verifyErrors: null | VerifyErrors) => {
                if (verifyErrors) {
                    captureException(verifyErrors)

                    await forceLogout()
                }
            })

            if ([401, 498, 499].includes(code)) {
                await forceLogout()
            }
        }
    }

    return response
}, (error) => Promise.reject(error))

export default axios
